import { useState } from "react";
import { Modal, Form } from "react-bootstrap";
import { Button } from "./Button";
import { InfoCircle, FileEarmarkText, PersonPlus } from "react-bootstrap-icons";

const DEFAULT_STORAGE_KEY = "spotsync-group-parkers-help-dismissed";

export type GroupParkersHelpDialogProps = {
  show: boolean;
  onClose: () => void;
  /** localStorage key for "don't show again". Defaults to spotsync-group-parkers-help-dismissed */
  storageKey?: string;
  /** Optional custom title (default: "Managing group parkers") */
  title?: string;
  /** Optional custom body content. If not provided, shows default group-parkers copy. */
  children?: React.ReactNode;
};

const defaultContent = (
  <div className="d-flex flex-column gap-4">
    <div className="bg-primary-subtle rounded p-3">
      <div className="d-flex align-items-center gap-2 mb-2">
        <FileEarmarkText className="text-primary fs-5" />
        <span className="fw-semibold">View contracts for your groups</span>
      </div>
      <p className="mb-0 text-muted small">
        Each group can have contract terms. Click on a group after adding a parker to view details.
      </p>
    </div>

    <div className="bg-primary-subtle rounded p-3">
      <div className="d-flex align-items-center gap-2 mb-2">
        <PersonPlus className="text-primary fs-5" />
        <span className="fw-semibold">Add parkers to groups</span>
      </div>
      <p className="mb-0 text-muted small">
        Use &quot;Add parker&quot; to add people to your plan and assign them to a group. You can move parkers between groups anytime (additional charges may apply).
      </p>
    </div>

    <div className="bg-light rounded p-3">
      <div className="fw-semibold mb-1">Quick summary</div>
      <ul className="mb-0 small text-muted ps-3">
        <li>Click a group to see its contract</li>
        <li>Add parkers and assign them to groups</li>
        <li>Edit or move parkers as your team changes</li>
      </ul>
    </div>
  </div>
);

export function GroupParkersHelpDialog({
  show,
  onClose,
  storageKey = DEFAULT_STORAGE_KEY,
  title = "Managing group parkers",
  children,
}: GroupParkersHelpDialogProps) {
  const [dontShowAgain, setDontShowAgain] = useState(false);

  const handleClose = (persistDontShow: boolean) => {
    if (persistDontShow) {
      try {
        localStorage.setItem(storageKey, "true");
      } catch {
        // ignore localStorage errors
      }
    }
    setDontShowAgain(false);
    onClose();
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleClose(dontShowAgain);
  };

  if (!show) return null;

  return (
    <Modal show={show} onHide={() => handleClose(false)} centered size="lg">
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="d-flex align-items-center gap-2">
          <InfoCircle className="text-primary" />
          {title}
        </Modal.Title>
      </Modal.Header>

      <Form onSubmit={handleSubmit}>
        <Modal.Body className="pt-2">
          {children ?? defaultContent}

          <Form.Group className="mt-3">
            <Form.Check
              id="dontShowAgain"
              type="checkbox"
              label="Don't show this again"
              checked={dontShowAgain}
              onChange={(e) => setDontShowAgain(e.target.checked)}
            />
          </Form.Group>
        </Modal.Body>

        <Modal.Footer className="border-0 pt-0">
          <Button type="submit" className="btn-main">
            Got it
          </Button>
        </Modal.Footer>
      </Form>
    </Modal>
  );
}

/** Returns whether the help dialog has been dismissed (don't show again) for the given key. */
export function isGroupParkersHelpDismissed(storageKey: string = DEFAULT_STORAGE_KEY): boolean {
  try {
    return localStorage.getItem(storageKey) === "true";
  } catch {
    return false;
  }
}
